/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { EscrowAnalysisViews, useServicerEscrowAnalysisContext } from '../../contexts/ServicerEscrowAnalysisContext';
import Styles from '../../css/ServicerEscrowAnalysis.module.css';
import EscrowMonthsTable from '../tables/EscrowMonthsTrialBalances';
import EscrowNumbersTable from '../tables/EscrowNumbers';
import { useAuthContext } from '../../contexts/AuthContext';
import { useAEAHistoryContext } from '../../contexts/AEAHistoryContext';
import { isStrEmptyOrNull, isObjEmptyOrNull, getTimestampAsDate } from '../../common/UtilityFunctions';
import { Button } from '@mui/material';
import { useCallback } from 'react';
import LoadingModal from '../modals/LoadingModal';

const AEAHistoryView = () => {
  const { fullAccess } = useAuthContext();
  const { setViewToShow, viewToShow } = useServicerEscrowAnalysisContext();
  const { AEAHistory, AEAHistoryLoading, AEAHistoryErrors, getAEAHistory } = useAEAHistoryContext();

  const handleShowAEAHistory = useCallback(
    async (appliedOccr: number) => {
      await getAEAHistory(appliedOccr);
      setViewToShow(EscrowAnalysisViews.AEA_HISTORY_VIEW);
    },
    [getAEAHistory],
  );

  const getEscrowAnalysisNumbers = useCallback(() => {
    let escrowAnalysisNumbers = {
      effectiveDate: getTimestampAsDate(AEAHistory.createOrUpdatePayment.data.dueDate),
      currentEscrowBalance: parseFloat(AEAHistory.createOrUpdatePayment.data.eaCurrentBalance).toFixed(2),
      estimatedBalance: parseFloat(AEAHistory.createOrUpdatePayment.data.eaEstimatedBalance).toFixed(2),
      projectedBalance: parseFloat(AEAHistory.createOrUpdatePayment.data.eaProjectedBalance).toFixed(2),
      shortSurplus: parseFloat(AEAHistory.createOrUpdatePayment.data.totalEscrowShortage).toFixed(2),
      prinInt: parseFloat(AEAHistory.createOrUpdatePayment.data.apparPi).toFixed(2),
      newEscrowPayment: parseFloat(AEAHistory.createOrUpdatePayment.data.eaNewEscrowPayment).toFixed(2),
      newMonthlyPayment: parseFloat(AEAHistory.createOrUpdatePayment.data.eaNewMonthlyPayment).toFixed(2),
      newEscrowPaymentWOShortSurplus: parseFloat(AEAHistory.createOrUpdatePayment.data.eaExpectedEscrowPayment).toFixed(
        2,
      ),
      newMonthlyPaymentWOShortSurplus: parseFloat(
        AEAHistory.createOrUpdatePayment.data.eaExpectedMonthlyPayment,
      ).toFixed(2),
      paymentChange: parseFloat(AEAHistory.createOrUpdatePayment.data.escrowShortageMonthlyRepaymentAmt).toFixed(2),
      refund: parseFloat(AEAHistory.createOrUpdatePayment.data.eaIntendedRefundAmount).toFixed(2),
      refundNotApplicable: AEAHistory.createOrUpdatePayment.data.eaRefundNotApplicable,
      escrowAnalysisTiCushion: AEAHistory.createOrUpdatePayment.data.escrowAnalysisTiCushion,
      escrowShortageRepayMonths: AEAHistory.createOrUpdatePayment.data.escrowShortageRepayMonths,
      runDate: getTimestampAsDate(AEAHistory.AEARunDateEvent?.activeDate),
      newMonthlyImpound: parseFloat(AEAHistory.impoundTotal.data.mnthlyImpAmt).toFixed(2),
      pitiDiff: parseFloat(AEAHistory.createOrUpdatePayment.data.eaPitiDiff).toFixed(2),
    };
    return escrowAnalysisNumbers;
  }, [AEAHistory]);

  const backToMenuButton = () => {
    return (
      <div className={isObjEmptyOrNull(AEAHistory) ? Styles.applybutton : Styles.aeahistorybacktomenubutton}>
        <Button
          fullWidth
          className={Styles.whitebutton}
          variant='outlined'
          size='large'
          onClick={() => {
            setViewToShow(EscrowAnalysisViews.MAIN_MENU_VIEW);
          }}
        >
          {'Back to Menu'}
        </Button>
      </div>
    );
  };

  const currPrevButtonsRow = () => {
    return (
      <div className={Styles.buttonrow}>
        <Button
          fullWidth
          variant='outlined'
          disabled={!fullAccess}
          onClick={() => {
            handleShowAEAHistory(1);
          }}
          size='large'
          className={Styles.whitebutton}
        >
          Previous
        </Button>
        <Button
          fullWidth
          variant='outlined'
          size='large'
          disabled={!fullAccess}
          onClick={() => {
            handleShowAEAHistory(0);
          }}
          className={Styles.whitebutton}
        >
          Current
        </Button>
      </div>
    );
  };

  const noAEAHistoryFoundView = () => {
    return (
      isObjEmptyOrNull(AEAHistory) && (
        <div className={Styles.noaeafound}>
          <p className={Styles.noaeafoundtext}>No applied AEA was found.</p>

          {!isStrEmptyOrNull(AEAHistoryErrors) && <p className={Styles.noaeafoundtext}>{AEAHistoryErrors}</p>}
          {backToMenuButton()}
        </div>
      )
    );
  };

  const AEAHistoryFoundView = () => {
    return (
      !isObjEmptyOrNull(AEAHistory) &&
      isStrEmptyOrNull(AEAHistoryErrors) && (
        <div className={Styles.grid}>
          <div className={Styles.smallcolumn}>
            <EscrowNumbersTable {...getEscrowAnalysisNumbers()}></EscrowNumbersTable>
            {backToMenuButton()}
          </div>
          <EscrowMonthsTable {...AEAHistory.trialBalances.data}></EscrowMonthsTable>
        </div>
      )
    );
  };

  const AEAHistoryResult = () => {
    return (
      <>
        {AEAHistoryFoundView()}
        {noAEAHistoryFoundView()}
      </>
    );
  };

  return (
    <>
      {viewToShow === EscrowAnalysisViews.MAIN_MENU_VIEW && currPrevButtonsRow()}
      {viewToShow === EscrowAnalysisViews.AEA_HISTORY_VIEW && !AEAHistoryLoading && AEAHistoryResult()}
      {AEAHistoryLoading && <LoadingModal open={true}></LoadingModal>}
    </>
  );
};

export default AEAHistoryView;
